/**
 * @fileoverview One plant, at full size: its name, where it is, how it is doing, its pictures and
 * everything the gardener has written about it (B136 + B138, design.md rev 12: one card face).
 *
 * The card assembles; it does not do. The photographs and which one is showing belong to usePlantPhotos, the picture itself to
 * PlantCardPhoto (with its arrows on it), the weather to WeekCaption, the plant's care to CareStrip, the tiles and their trays
 * to CardTiles. What is left here is the order those things go in, the one open tray, and the dialogs they open.
 *
 * Top to bottom: the name row, which is the Edit button (a small muted pencil at its end; the whole row a 44px target; in
 * someone else's garden and on an archived card it is plain words); the info line, the bed as a link in the link colour and the
 * stage as a rounded-square tag with a select-style down-chevron (PlantStageLine); the planting strip (PlantingDatesLine: the
 * server's current or next window as a tinted strip that unfolds every window under it; the warm "plant now" look when the
 * server says a window is open; a "Not sure" plant gets a small "Planted yet?" chip); the photo, exactly as built (4:3, its
 * glass buttons at the top right, the arrows, the date and note strip; a plant with none gets the placeholder, AddFirstPhoto);
 * the weather as one caption line ending "... more" (WeekCaption), or for a Planning plant or a sheltered bed the note that says
 * why it has none (NoWeekNote, with the one-tap "Plant now", PlantNowButton, E5); the Care strip (CareStrip); then the tiles
 * (CardTiles): Share filled and Public, then To-dos, Notes and Doctor. There is no "..." menu: Archive, Delete and Merge into...
 * are inside the Edit form (PlantFormDialog). At most one tray is open on a card at a time (the planting windows, the weather's
 * story, Care, the to-dos or the notes); opening one scrolls it into view.
 *
 * The photo's own tools are on the picture, at its top right, as photo apps put them (add a photo, edit this photo, and the eye:
 * Hide from my public page / Show on my public page), and its arrows are on its sides. That eye is the garden owner's alone
 * (B37): for a member tending someone else's garden it shows the owner's choice, greyed, and a tap says "Only the garden's owner
 * can change this." (useOwnerOnly).
 *
 * The example plant sign-up may put in (mysqlUser.example_plant_id) is named "Tomato", with a small "Example" mark beside it,
 * whatever its variety.
 *
 * Where: PlantGrid's cards view, and PlantQuickLook.
 * API: the plants brief (usePlantsBrief: the plant's planting stage, its week and caption, whether it has planting dates, its
 * sharing, its to-dos due today, its Care facts, and whether Grow knows its crop; one read for the garden, no read of their own;
 * the crop's timings stay on the server); everything else through its parts and dialogs (the Care tray reads GET
 * /plant-about/:id itself, only while it is open).
 * Settings: Profile's card photos choice (CARD_PHOTOS_KEY, on the device); mysqlUser.example_plant_id.
 * Words (en/es): plants.card.* (example.*, edit, care, tiles.*, noWeek.*, photos.*); the caption and the Care facts are the brief's.
 *
 * The name is the backend's one name (lib/plantName, services/grow/plantNaming: "Devil's Ear Lettuce", "Butterhead Lettuce"),
 * with the day it was added in bold when a twin shares its bed (NameParts); the bed is on its own line under the name, not said
 * again in it. The kind lives in the Care tray. The card composes no name.
 *
 * @module PlantCard
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Eye, EyeOff, MapPin, Pencil, Plus, Stethoscope } from 'lucide-react';
import type { PlantPhoto } from '@/shared/types/photo';
import { CARD_PHOTOS_KEY, CARD_PHOTOS_VALUES, useLocalPref } from '@/shared/lib/localPref';
import { Card, CardContent, CardDescription, CardHeader } from '@/shared/components/ui/card';
import { CardNameRow } from '@/shared/components/ui/CardNameRow';
import { TileButton } from '@/shared/components/ui/TileButton';
import { usePlantDoctorDoor } from '@/features/plant-doctor/usePlantDoctorAccess';
import { plantSubject } from '@/shared/api/subject';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { Pill } from '@/shared/components/ui/Pill';
import { setPhotoHiddenFromPublic } from '@/shared/api/photos';
import { Plant } from '@/shared/types/plant';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { toast } from '@/shared/components/ui/use-toast';
import { errorMessage } from '@/shared/lib/errorMessage';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';
import { plantName, plantShown, plantTitle, shareWords } from '@/shared/lib/plantName';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import { useGardenSeason } from '@/shared/hooks/useTodayBrief';
import { usePlantPhotos } from '../lib/usePlantPhotos';
import { plantSubtitle } from '../lib/plantSubtitle';
import { shareDraftKey, useResumeShare } from '../lib/shareDraft';
import { plantCaptionStarter } from '../lib/captionStarter';
import { PlantCardPhoto } from './card/PlantCardPhoto';
import { AddFirstPhoto } from './card/AddFirstPhoto';
import { PlantStageLine } from './card/PlantStageLine';
import { PlantingDatesLine } from './card/PlantingDatesLine';
import { PlantNowButton } from './card/PlantNowButton';
import { NoWeekNote } from './card/NoWeekNote';
import { WeekCaption } from './card/WeekCaption';
import { CareStrip } from './card/CareStrip';
import { CardTiles, type CardTray } from './card/CardTiles';
import { PlantDoctor } from './card/PlantDoctor';
import { PhotoUploadDialog } from './dialogs/PhotoUploadDialog';
import { SharePlantDialog } from './dialogs/SharePlantDialog';
import { bringIntoView } from '@/shared/hooks/useBringIntoView';

/** The plant, and what the card may offer to do with it. */
interface PlantCardProps {
  /** The plant, as the garden's list has it (photos and notes included). */
  plant: Plant;
  /** Opens the edit dialog (the name row is its button), where Archive, Delete and Merge into... are. Omitted where editing is not offered. */
  onEdit?: (plant: Plant) => void;
  /**
   * True when this plant has been taken out of the garden.
   *
   * An archived card still shows everything — the photographs and the journal
   * are the reason archiving is not deleting — but nothing on it writes. You
   * could photograph a plant you had removed, and add notes to it, which is
   * either a mistake or an argument that it was never archived.
   */
  archived?: boolean;
  /**
   * A word under the name for a plant the name alone does not identify — two
   * sowings of the same variety in the same bed. Null where it stands alone.
   */
  sublabel?: string | null;
}

/**
 * One plant as a card: its name (the Edit button), its bed and stage on one line, the planting strip, its photographs with their
 * own tools, the week's caption, the Care strip and the tiles (Share, Public, To-dos, Notes, Doctor).
 *
 * @param props - PlantCardProps.
 */
export const PlantCard: React.FC<PlantCardProps> = ({
  plant, onEdit, archived = false, sublabel = null,
}) => {
  /** The signed-in gardener's account (the example plant's id, the photo paths' user id). */
  const { mysqlUser } = useAuth();
  /** The app's words, in the gardener's language. */
  const { reactTranslate: t } = useTranslation();
  /** In someone else's garden: why the photo's eye is locked (B37), and that the name does not open Edit; null/false in the person's own. */
  const { lockedReason, tending } = useOwnerOnly();
  /** The plant's photos and what can be done to them (lib/usePlantPhotos). */
  const photos = usePlantPhotos(plant);
  /** The example plant from sign-up: "Tomato", marked "Example", and no line under it. */
  const isExample = mysqlUser?.example_plant_id != null && mysqlUser.example_plant_id === plant.plant_obj_id;
  /** The card's name as text: the backend's one name (lib/plantName), or "Tomato" for the example plant. */
  const title = isExample ? t('plants.card.example.name') : plantShown(plant);
  /** The name as pieces: what tells it from another of the same name (its bed, the day added) in bold. */
  // The bed is on its own line under the name: not said again in it.
  const titleParts = isExample ? [{ text: title }] : plantTitle(plant, { bedShown: true });
  /** The share frame's two words: the crop's name and the plant's own words (lib/plantName shareWords). */
  const share = shareWords(plant);
  /** The line under the name (the view's extra), or null for none. */
  const subtitle = isExample ? null : plantSubtitle(plant, sublabel);
  /** Profile's card photos choice, kept on the device: 'peek' shows the next picture's edge. */
  const [cardPhotos] = useLocalPref(CARD_PHOTOS_KEY, 'peek', CARD_PHOTOS_VALUES);
  /** The name opens Edit: where editing is offered and the plant is in the gardener's own garden. */
  const edit = tending ? undefined : onEdit;
  /**
   * What a picture says about itself, by the note it points at. Each slide
   * of the carousel carries its own, so this is a lookup rather than a value.
   *
   * @param photo - The picture.
   * @returns Its words, or null.
   */
  const noteFor = React.useCallback(
    (photo: PlantPhoto): string | null =>
      (photo.note_id ? plant.notes?.find((n) => n.note_id === photo.note_id)?.content ?? null : null),
    [plant.notes]
  );

  /**
   * Hides the photo in view from the public pages, or shows it again, and
   * puts the answer on the card at once.
   *
   * @returns Resolves when the change is made or has failed (a failure is a toast).
   */
  const toggleHiddenFromPublic = async (): Promise<void> => {
    const shown = photos.current;
    if (!shown?.photo_id) return;
    // A member: only why it is locked (B37).
    if (lockedReason) {
      toast({ description: lockedReason });
      return;
    }
    try {
      const hidden = await setPhotoHiddenFromPublic(plant.plant_obj_id, shown.photo_id, !shown.hidden_from_public);
      photos.received({ ...shown, hidden_from_public: hidden });
    } catch (error) {
      // A failure on our side is the standard sentence, reported to Sentry (errorMessage, D31); a refusal, ours.
      toast({ title: errorMessage(error, t('plants.card.photos.hideFailed')), variant: 'destructive' });
    }
  };

  /** Add a photo is open. */
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  /** The photo in view is open in its editor. */
  const [isEditPhotoOpen, setIsEditPhotoOpen] = useState(false);
  /** The share studio is open. */
  const [isShareOpen, setIsShareOpen] = useState(false);
  // The garden's season for the caption starter: the server's, read once the Share screen opens (or from Today's read).
  const gardenSeason = useGardenSeason(isShareOpen);
  // A share of this plant in progress when the tab was reloaded (Android, back from Instagram): its sheet opens again where it was.
  useResumeShare(shareDraftKey('plant', plant.plant_obj_id), () => setIsShareOpen(true));
  /** The Plant Doctor window is open (opened from the Doctor tile). */
  const [isCheckupOpen, setIsCheckupOpen] = useState(false);
  /** The plant as the tiles' subject. */
  const subject = React.useMemo(() => plantSubject(plant.plant_obj_id), [plant.plant_obj_id]);
  /** The one tray open on the card, or none: the planting windows, the weather's story, Care, the to-dos or the notes. */
  const [tray, setTray] = useState<CardTray>(null);
  /** The card, to bring the open tray's door into view. */
  const cardRef = React.useRef<HTMLDivElement | null>(null);

  // A tray that opens under a tile or a line at the screen's edge is brought into view, so the door that opened it is not lost.
  React.useEffect(() => {
    if (tray) bringIntoView(cardRef.current?.querySelector('[aria-expanded="true"]'));
  }, [tray]);

  /**
   * Opens a tray, or shuts it when it is the open one (one tray at a time across the card).
   *
   * @param name - The tray a strip or caption opens.
   * @returns A function for the strip's onClick or the caption's onToggle.
   */
  const trayToggle = (name: Exclude<CardTray, null>) => (): void => setTray((open) => (open === name ? null : name));

  /**
   * The app's own user id, which photo paths are built on. It used to be
   * parseInt of the Firebase uid, which is letters and digits, so NaN.
   */
  const userId = mysqlUser?.user_id ?? 0;

  /** Whether it has a picture: Public, Share and the checkup wait for one. */
  const hasPhotos = photos.photos.length > 0;
  /** The Doctor tile's state: the lock, the dimming and where a tap goes (the server's plan answer; the app decides nothing). */
  const door = usePlantDoctorDoor(hasPhotos, () => setIsCheckupOpen(true));
  /** The newest picture's day, which the check-up looks at. */
  const newestPhotoOn = photos.photos.map((photo) => photo.date_taken).filter(Boolean).sort().pop() ?? null;

  /*
   * Whether the card has a week to show at all: the server says, by sending
   * one (the plants brief's week; audit batch 4, A23). A sheltered bed has
   * none: the weather does not reach it. Nor does a plant at Planning: nothing
   * is in the ground yet, so the outlook leaves it out, and its card says when
   * to plant it instead (PlantingDatesLine). An archived plant has none
   * either: it is out of the garden. The app does not guess a week for a plant
   * the server left out.
   */
  const { forPlant, brief } = usePlantsBrief();
  /** This plant in the plants brief (its stage, week, planting dates, sharing, to-dos and Care facts), or null before it is read. */
  const said = forPlant(plant.plant_obj_id);
  /**
   * Whether the card offers the Care strip: the server says whether Grow knows the plant's crop (W24 Job A), in the brief's
   * `about` for a live plant and in its `aboutArchived` ids for an archived one (the brief's plants hold live plants only).
   * False until the brief is read.
   */
  const hasAbout = archived
    ? brief?.aboutArchived.includes(plant.plant_obj_id) === true
    : said?.about === true;
  /** At Planning, as recorded: nothing is in the ground yet. */
  const atPlanning = said?.stage === 'planning';
  /** It has a week to show: the brief sent one, and it is not archived (AB 30). */
  const week = !archived ? said?.week ?? null : null;
  /** The stage sheet, held here so a Not sure plant's "Planted yet?" chip (PlantingDatesLine) can open it. 'growing' offers only the stages past Planning. */
  const [stageSheet, setStageSheet] = useState<'closed' | 'open' | 'growing'>('closed');

  return (
    <>
      <Card ref={cardRef} className="relative flex h-full w-full max-w-sm flex-col overflow-hidden rounded-sm shadow-lg transition-all duration-200">
        {/* The name first: it is what the card is about, and it is the Edit button, with a small muted pencil at its end. */}
        <CardHeader tight>
          <CardNameRow
            parts={titleParts}
            after={isExample ? <Pill size="sm" tone="outline">{t('plants.card.example.mark')}</Pill> : undefined}
            onEdit={edit ? () => edit(plant) : undefined}
            ariaLabel={t('plants.card.edit', { plant: title })}
          />
          <CardDescription className="flex flex-col gap-0.5">
            {/* The type line only when there is one: an empty line kept for pictures to line up across a row of cards left a gap
                under every name on a phone. */}
            {subtitle && <span className="block">{subtitle}</span>}
            {/* The bed (a link, in the link colour, underlined) and its stage (a tag with a down-chevron: tap to change) on one
                line that never wraps: the bed's name ends in an ellipsis when they do not fit, the stage stays whole. The stage is
                changed here alone: photos carry none. */}
            <div className="flex min-h-8 min-w-0 flex-nowrap items-center gap-2">
              <Link
                to={`/beds/${plant.location.id}`}
                className={cn('flex min-h-8 min-w-0 items-center gap-0.5 text-sm font-semibold text-primary underline decoration-primary/50 underline-offset-[3px]')}
              >
                <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="min-w-0 truncate">{plant.location.name}</span>
              </Link>
              <span aria-hidden="true" className="shrink-0">·</span>
              <div className="shrink-0">
                <PlantStageLine
                  plant={plant}
                  editable={!archived}
                  sheetOpen={stageSheet !== 'closed'}
                  onSheetOpenChange={(next) => setStageSheet(next ? 'open' : 'closed')}
                  growing={stageSheet === 'growing'}
                />
              </div>
            </div>
          </CardDescription>
          {/* The planting line (the current or next window, any stage), a strip that opens every window under it. */}
          {!archived && (
            <div className="!mt-1.5">
              <PlantingDatesLine
                plant={plant}
                open={tray === 'planting'}
                onToggle={trayToggle('planting')}
                onPlantedYet={() => setStageSheet('open')}
              />
            </div>
          )}
        </CardHeader>

        {/* A plant with pictures gets the frame and the strip. A plant with
            none gets one row, or — when it is archived and cannot be
            photographed anyway — nothing. */}
        {hasPhotos ? (
          <>
            <PlantCardPhoto
              photos={photos.photos}
              selected={photos.selected}
              onSelect={photos.select}
              isLoading={photos.isLoading}
              noteFor={noteFor}
              plantName={plantName(plant)}
              plantObjId={archived ? undefined : plant.plant_obj_id}
              onEditPhoto={archived ? undefined : () => setIsEditPhotoOpen(true)}
              editHint={false}
              peek={cardPhotos === 'peek'}
              actions={archived ? undefined : (
                <>
                  {/* Adding a photo, the more common, first. */}
                  <PhotoButton label={t('plants.card.photos.add')} onClick={() => setIsUploadOpen(true)}>
                    {/* A camera with a plus: add a picture. */}
                    <span className="relative" aria-hidden="true">
                      <Camera className="h-3.5 w-3.5" />
                      <Plus className="absolute -right-1 -top-1 h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  </PhotoButton>
                  <PhotoButton label={t('plants.card.photos.editThis')} onClick={() => setIsEditPhotoOpen(true)}>
                    <Pencil className="h-3 w-3" aria-hidden="true" />
                  </PhotoButton>
                  {/*
                    * Whether the public page shows this photo: one eye, a tap turns it (owner, 2026-10-15: one button,
                    * the eye, where the ⋯ menu was). Open eye: shown; crossed eye: hidden. On a public plant, where it
                    * means something, and on a photo already hidden whatever the plant, so it can always be shown again
                    * (the audit, 2026-10-16: a private plant's hidden photo had no way back).
                    */}
                  {(Boolean(plant.is_public) || Boolean(photos.current?.hidden_from_public)) && (
                    <PhotoButton
                      label={photos.current?.hidden_from_public ? t('plants.card.photos.show') : t('plants.card.photos.hide')}
                      aria-pressed={!photos.current?.hidden_from_public}
                      aria-disabled={lockedReason ? true : undefined}
                      title={lockedReason ?? undefined}
                      className={cn(lockedReason && 'opacity-60')}
                      onClick={() => { void toggleHiddenFromPublic(); }}
                    >
                      {photos.current?.hidden_from_public
                        ? <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                        : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
                    </PhotoButton>
                  )}
                </>
              )}
            />

          </>
        ) : !archived ? (
          <>
            <AddFirstPhoto
              onTakePhoto={() => setIsUploadOpen(true)}
              varietyName={plantName(plant)}
            />

          </>
        ) : null}

        {/* The weather as one caption line (every card with a week); a Planning plant or a sheltered bed says why it has none. */}
        {week?.caption && <WeekCaption plantObjId={plant.plant_obj_id} week={week} open={tray === 'week'} onToggle={trayToggle('week')} />}
        {!week && !archived && (atPlanning || plant.location.sheltered) && (
          <NoWeekNote kind={atPlanning ? 'planning' : 'sheltered'}>
            {/* E5: a Planning plant's one-tap "Plant now" (the server picks the stage); no dialog, an Undo after. */}
            {atPlanning && said?.plantNow && <PlantNowButton plant={plant} plantNow={said.plantNow} />}
          </NoWeekNote>
        )}

        {/* Care: its facts are the server's; a tap opens the temperatures, water and feeding right under the line. */}
        {hasAbout && (
          <CareStrip
            plantObjId={plant.plant_obj_id}
            plantName={plantName(plant)}
            kindName={plant.names.kindName || null}
            line={said?.care?.line ?? null}
            open={tray === 'care'}
            onToggle={trayToggle('care')}
          />
        )}

        <CardContent className="p-0">
          <CardTiles
            subject={subject}
            name={isExample ? title : plantName(plant)}
            hasPhotos={hasPhotos}
            archived={archived}
            showPublic={Boolean(edit)}
            opens={said?.sharing?.visits ?? 0}
            sharing={said ? said.sharing ?? undefined : null}
            dueToday={said?.todos?.dueToday ?? false}
            todoCount={said?.todos?.open ?? 0}
            notes={plant.notes}
            plantPublic={Boolean(plant.is_public)}
            tray={tray}
            onTray={setTray}
            onShare={() => setIsShareOpen(true)}
            third={(
              <TileButton
                size="small"
                icon={<Stethoscope />}
                locked={door.locked}
                dim={door.dim}
                to={door.to ?? undefined}
                title={door.title}
                aria-label={t('plants.diagnose.title')}
                onClick={door.press}
              >
                {t('plants.card.tiles.doctor')}
              </TileButton>
            )}
          />
        </CardContent>
      </Card>

      {!archived && (
        <PlantDoctor
          open={isCheckupOpen}
          onOpenChange={setIsCheckupOpen}
          plantObjId={plant.plant_obj_id}
          newestPhotoOn={newestPhotoOn}
          plantName={plantName(plant)}
        />
      )}

      <SharePlantDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        plantObjId={plant.plant_obj_id}
        name={share.name}
        variety={share.variety}
        // The sheet's first title names the whole plant ("Share Loose-leaf Lettuce"), not only its own words ("Loose-leaf").
        title={isExample ? undefined : t('plants.share.titleFor', { plant: plantName(plant) })}
        photos={photos.photos}
        startPhotoId={photos.photos[photos.selected]?.photo_id ?? null}
        onAddPhoto={() => setIsUploadOpen(true)}
        captionStarter={isShareOpen ? plantCaptionStarter(plant, gardenSeason, t) : ''}
      />

      <PhotoUploadDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        userId={userId}
        plantObjId={Number(plant.plant_obj_id)}
        onPhotoUploaded={photos.received}
      />

      <PhotoUploadDialog
        isOpen={isEditPhotoOpen}
        onClose={() => setIsEditPhotoOpen(false)}
        userId={userId}
        plantObjId={Number(plant.plant_obj_id)}
        onPhotoUploaded={photos.received}
        editMode={true}
        existingPhoto={photos.current}
        onPhotoDeleted={photos.removed}
      />
    </>
  );
};

